import { number, z, ZodSchema, type ZodErrorMap } from 'zod';
import type { IGameState, IStage } from '.';

export const reflectoraPlayerColors = ['blue', 'pink', 'yellow', 'orange', 'green'];

export const ReflectoraDataSchema = z
	.object({
		step1Themes: z.array(z.string()).default([]),
		step2Feelings: z.array(z.string()).default([]),
		step3Conclusions: z.array(z.string()).default([]),
	})
	.default({ step1Themes: [], step2Feelings: [], step3Conclusions: [] });

export type Stage = IStage | 'answer-questions' | 'play-rounds';

export type ReflectoraPlayerState = {
	id: string;
	/**
	 * when false this player will get skipped by game logic,
	 * allowing them to leave and come back at a later time without blocking the entire game
	 */
	active: boolean;
	phase: number;
	roundThemeIndex?: number;
	themesPresented: number[];
	themeScores: number[];
	hasAmulet: boolean;
	hasMonster: boolean;
	/**
	 * when a player has 4 good cards and 4 bad cards they get a +2 score increase.
	 * This should only happend once, so we keep track of wether we granted the points here.
	 */
	name: string;
	color: string;
	score: number;
	reflectionScore: number;
	answers: (string | null | undefined)[];
	lastScreen: ReflectoraUpdate;

	// indexed as evaluations[playerIndex][themeIndex]
	evaluations: { koers: string; emotions: string[] }[][];
	// evaluations: z.array(z.array(z.object({ koers: z.string(), emotions: z.array(z.string()) }))),

	// indexed as evaluations[playerIndex][themeIndex]
	conclusions: { conclusion: string; conclusionText: string; usedAmulet: boolean }[][];
	// indexed as notes[themeIndex]
	// when sent to the client we convert this to just a string holding the notes for the currently selected theme
	notes: string[];
	amuletScore: number;
};

export type ReflectoraState = {
	stage: Stage;
	roundIndex: number;
	lastRound: boolean;
	extraTurn: boolean;

	players: ReflectoraPlayerState[];
	currentPlayerIndex: number;
	currentRoll: number;
};

const ReflectoraClientPlayerSchema = z.object({
	name: z.string(),
	color: z.string(),
	score: z.number(),
	reflectionScore: z.number(),
	answers: z.array(z.string().nullable().optional()),
	hasMonster: z.boolean(),
	hasAmulet: z.boolean(),
	themeNotes: z.string(),
});
export type ReflectoraClientPlayer = z.infer<typeof ReflectoraClientPlayerSchema>;

export const ReflectoraUpdateSchema = z.discriminatedUnion('kind', [
	z.object({ kind: z.literal('show-login-screen'), failed: z.boolean().optional() }),
	z.object({ kind: z.literal('show-register-screen') }),
	z.object({ kind: z.literal('show-wait-screen'), message: z.string().optional(), playerName: z.string().optional() }),

	z.object({
		kind: z.literal('show-questions-screen'),
		questions: z.array(z.string()),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),

	// phase 1
	z.object({ kind: z.literal('show-turnstart-screen') }),
	z.object({
		kind: z.literal('show-chancecard-screen'),
		action: z.string(),
		effect: z.enum(['confetti', 'red-flash']).optional(),
		options: z.array(z.object({ label: z.string(), value: z.string() })),
		card: z.string(),
		message: z.string(),
	}),
	z.object({
		kind: z.literal('show-choose-question-screen'),
		questions: z.array(z.object({ question: z.string(), index: z.number(), answer: z.string().nullable().optional() })),
	}),
	z.object({
		kind: z.literal('move-boat-screen'),
		phase: z.number(),
	}),
	z.object({
		kind: z.literal('show-present-answer-screen'),
		number: z.number(),
		question: z.string(),
		answer: z.string().nullable().optional(),
	}),
	z.object({
		kind: z.literal('show-moneycard-screen'),
		action: z.string(),
		effect: z.enum(['confetti', 'red-flash']).optional(),
		card: z.string(),
		message: z.string(),
	}),

	// phase 2
	z.object({
		kind: z.literal('show-evaluate-question-screen'),
		question: z.string(),
		answer: z.string().nullable().optional(),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-evaluation-results-screen'),
		question: z.string(),
		answer: z.string().nullable().optional(),
		results: z.array(
			z.object({
				playerName: z.string(),
				koerskaart: z.string(),
				emotions: z.array(z.string()),
			}),
		),
	}),

	// phase 3
	z.object({
		kind: z.literal('show-conclude-question-screen'),
		question: z.string(),
		answer: z.string().nullable().optional(),
		timerFrom: z.number(),
		timerTo: z.number(),
	}),
	z.object({
		kind: z.literal('show-conclusion-results-screen'),
		question: z.string(),
		answer: z.string().nullable().optional(),
		results: z.array(
			z.object({
				playerName: z.string(),
				conclusion: z.string(),
				conclusionText: z.string(),
			}),
		),
	}),
	z.object({
		kind: z.literal('show-write-conclusion-screen'),
		question: z.string(),
		answer: z.string().nullable().optional(),
	}),

	z.object({
		kind: z.literal('show-conclude-wait-screen'),
	}),
	z.object({
		kind: z.literal('show-conclude-rating-screen'),
		rating: z.number(),
		playerName: z.string().optional(),
	}),

	z.object({
		kind: z.literal('show-results-screen'),
		players: z.array(
			z.object({
				name: z.string(),
				color: z.string(),
				score: z.number(),
				scoreReflection: z.number(),
				reflectionColors: z.array(z.string().nullable()), //reflectionColors: ('green' | 'red' | 'yellow')[]
			}),
		),
	}),

	z.object({
		kind: z.literal('update-players-state'),
		players: z.array(z.object({ name: z.string(), color: z.string() })),
	}),
	z.object({
		kind: z.literal('update-player-state'),
		player: ReflectoraClientPlayerSchema,
	}),

	z.object({
		kind: z.literal('trigger-effect'),
		effect: z.enum(['confetti', 'red-flash']),
		parameters: z.array(z.string()).optional(),
		text: z.string().optional(),
	}),
	z.object({
		kind: z.literal('show-rate-conclusion'),
		theme: z.number(),
		rating: z.number(),
	}),
]);
export type ReflectoraUpdate = z.infer<typeof ReflectoraUpdateSchema>;

export const ReflectoraAdminUpdateSchema = z.discriminatedUnion('kind', [
	//
	z.object({ kind: z.literal('update-state'), state: z.any() }),
]);
export type ReflectoraAdminUpdate = z.infer<typeof ReflectoraAdminUpdateSchema>;

export const ReflectoraMessageSchema = z.discriminatedUnion('kind', [
	// TODO: make it so this can only be sent by admin
	z.object({ kind: z.literal('start-game') }),
	z.object({ kind: z.literal('force-restart-round') }),
	z.object({ kind: z.literal('set-is-last-round'), lastRound: z.boolean() }),
	z.object({ kind: z.literal('set-player-active'), playerId: z.string(), active: z.boolean() }),
	z.object({ kind: z.literal('end-questions') }),
	z.object({ kind: z.literal('finish-presenting') }),
	z.object({ kind: z.literal('submit-movement') }),
	z.object({ kind: z.literal('open-chest') }),
	z.object({ kind: z.literal('finish-evaluation') }),
	z.object({ kind: z.literal('finish-conclusion') }),
	z.object({ kind: z.literal('rate-conclusion'), playerId: z.string(), themeIndex: z.number(), rating: z.number() }),

	// TODO: make it so this can be sent by any player
	z.object({ kind: z.literal('update-player-data'), playerId: z.string(), name: z.string(), color: z.string() }),
	z.object({ kind: z.literal('remove-player-data'), playerId: z.string() }),
	z.object({ kind: z.literal('submit-answer'), playerId: z.string(), answer: z.string(), answerIndex: z.number() }),
	z.object({ kind: z.literal('submit-diceroll'), playerId: z.string(), roll: z.number() }),
	z.object({ kind: z.literal('choose-chancecard-response'), playerId: z.string(), response: z.string() }),
	z.object({ kind: z.literal('end-moneycard'), playerId: z.string() }),
	z.object({ kind: z.literal('submit-answer-to-present'), playerId: z.string(), questionNumber: z.number() }),
	z.object({ kind: z.literal('save-notes'), playerId: z.string(), notes: z.string() }),

	z.object({
		kind: z.literal('submit-question-evaluation'),
		type: z.literal('submit').or(z.literal('update')),
		playerId: z.string(),
		koerskaart: z.string(),
		emotions: z.array(z.string()),
	}),
	z.object({
		kind: z.literal('submit-question-conclusion'),
		playerId: z.string(),
		conclusion: z.string(),
		conclusionText: z.string(),
	}),
	z.object({ kind: z.literal('submit-conclusion-text'), playerId: z.string(), conclusion: z.string() }),
	z.object({
		kind: z.literal('use-amulet'),
		playerId: z.string(),
	}),
]);
export type ReflectoraMessage = z.infer<typeof ReflectoraMessageSchema>;
